import { MutationHandlingServiceV2, NotificationService } from '@akebono/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, takeUntil } from 'rxjs/operators';
import { Limits } from 'src/app/services/limits.service';

import { AutoLotView } from '../../../../../const';
import {
  LotProductionDateCheckRequestCreateGQL,
  LotProductionDateFetchGQL,
  LotSource,
  LotTypeEnum,
  UserLotQuery,
} from '../../../../graphql/service/graphql-auto-main-service';
import { CheckProductionDatePayloadService } from '../../services/check-production-date-payload.service';
import { ProductionDatePayload } from '../../types/production-date-payload.type';
import { ProductionDateLotViewType } from '../../types/production-date-lot-view.type';
import { normalizeFrame } from 'src/app/utils/frame.util';

@Component({
  selector: 'app-lot-check-production-date-input',
  templateUrl: './lot-check-production-date-input.component.html',
  styleUrls: ['./lot-check-production-date-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotCheckProductionDateInputComponent implements OnInit, OnDestroy {
  @Input() lot: AutoLotView;
  @Input() lotType: LotTypeEnum;
  @Input() lotSource: LotSource;
  @Input() fullVin?: boolean;
  @Input() limits: Limits;
  @Input() currentUser: UserLotQuery['currentUser'];
  @Output() inputFocus = new EventEmitter<void>();

  form: FormGroup;

  checkFail = false;
  loading$ = of(false);
  payload$: Observable<ProductionDatePayload>;
  canCheckProductionDate$ = of(true);
  cantRequestCheckProductionDate$ = of(false);

  private destroy$ = new Subject<void>();

  allowedCompanies = [
    'TOYOTA',
    'NISSAN',
    'MAZDA',
    'HONDA',
    'SUBARU',
    'DAIHATSU',
    'SUZUKI',
    'ISUZU',
    'MITSUBISHI',
    'HINO',
    'LEXUS',
    'DATSUN',
  ];
  allowed = false;

  constructor(
    private fb: FormBuilder,
    private mhs: MutationHandlingServiceV2,
    private notificationService: NotificationService,
    private checkProductionDatePayloadService: CheckProductionDatePayloadService,
    private lotProductionDateFetchGQL: LotProductionDateFetchGQL,
    private lotProductionDateCheckRequestCreateGQL: LotProductionDateCheckRequestCreateGQL,
  ) {
    this.form = this.fb.group({
      vinManufacture: [null, [Validators.required]],
    });
  }

  ngOnInit(): void {
    const isSanctioned = !!this.lot?.isRestricted;
    this.allowed =
      (this.allowedCompanies.includes(this.lot.company) && !isSanctioned) ||
      this.currentUser.isAuctioneer;

    this.form.patchValue({
      vinManufacture: normalizeFrame(this.lot.vin),
    });

    if (this.fullVin && this.lot.vin && !this.lot.productionDate) {
      this.checkDateOfManufacture();
    }

    this.payload$ = this.checkProductionDatePayloadService.payload$;
    this.checkProductionDatePayloadService.emit(this.lot);
    this.canCheckProductionDate$ = combineLatest([of(this.currentUser), this.payload$]).pipe(
      map(([currentUser, payload]) => {
        const payloadExists = Boolean(payload?.length);

        if (currentUser.canCheckProductionDate || currentUser.canRecheckProductionDate) {
          if (payloadExists) {
            return currentUser.canRecheckProductionDate;
          } else {
            return true;
          }
        } else {
          return false;
        }
      }),
    );

    this.cantRequestCheckProductionDate$ = combineLatest([
      of(this.currentUser),
      this.limits?.productionDateCheckRequestLimitReached$,
    ]).pipe(
      map(([currentUser, productionDateCheckRequestLimitReached]) => {
        if (currentUser.isAuctioneer) {
          return false;
        }

        return productionDateCheckRequestLimitReached ?? false;
      }),
      shareReplay(1),
    );
  }

  submit(): void {
    if (this.checkFail && !this.currentUser.isAuctioneer) {
      this.createProductionDateCheckRequest();
    } else {
      this.checkDateOfManufacture();
    }
  }

  checkDateOfManufacture(): void {
    const mutationRef = this.mhs.mutate(
      this.lotProductionDateFetchGQL,
      {
        input: {
          lotId: this.lot.id,
          lotType: this.lotType,
          lotSource: this.lotSource,
          vin: normalizeFrame(this.form.value.vinManufacture || this.lot.vin),
        },
      },
      {
        failureTranslationKey: 'PRODUCTION_DATE_CHECK_ERROR',
        renderSuccess: false,
      },
    );

    this.loading$ = mutationRef.loading;
    mutationRef.data
      .pipe(
        map((data) => {
          this.handleProductionDateResult(data?.lotProductionDateFetch?.lot);
        }),
        catchError(() => {
          this.checkFail = true;
          return of(null);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  handleProductionDateResult(lot: ProductionDateLotViewType | null): void {
    this.checkProductionDatePayloadService.emit(lot);
    const payload = this.checkProductionDatePayloadService.compilePayload(lot);
    if (Boolean(payload.length)) {
      this.notificationService.renderSuccess('MUTATION_SUCCEEDED');
      document
        .getElementById('lots-check-production-date-result')
        ?.scrollIntoView({ behavior: 'smooth' });
    } else {
      this.notificationService.renderError('NO_PRODUCTION_DATA');
    }
  }

  createProductionDateCheckRequest(): void {
    this.loading$ = this.mhs.mutate(
      this.lotProductionDateCheckRequestCreateGQL,
      {
        input: {
          lotId: this.lot.id,
          lotType: this.lotType,
          lotSource: this.lotSource,
          vin: normalizeFrame(this.form.value.vinManufacture),
        },
      },
      {
        refetch: false,
        successTranslationKey: 'USER_REQUESTS.CREATE_SUCCESS',
        failureTranslationKey: 'USER_REQUESTS.CREATE_FAIL',
      },
    ).loading;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
